import { formatEstimatedCostUsd } from "./aiUsage";
import type { ProductivityCurrency } from "../../../shared/productivityCurrency";

export const DEFAULT_PRODUCTIVITY_HOURLY_RATE_USD = 75;

// Activities that contribute to a workflow's dollar value. Monitor (autofix/
// autotune) is intentionally excluded: we can't count episodes accurately yet.
const VALUE_ACTIVITIES = ["build", "run"] as const;
export type ValueActivity = (typeof VALUE_ACTIVITIES)[number];

export type WorkflowValueWorkflow = {
  id: string;
  name: string;
  spaceId: string;
  estimatedMinutesSaved: number | null;
  estimatedBuildMinutesSaved: number | null;
  timeSavedSource: "llm" | "manual" | null;
  timeSavedBuildSource: "llm" | "manual" | null;
};

type WorkflowActivityValue = {
  eventCount: number;
  minutesPerEvent: number | null;
  source: "llm" | "manual" | null;
  grossValueUsd: number | null;
};

export type WorkflowAiValueRow = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  byActivity: Record<ValueActivity, WorkflowActivityValue>;
  totalGrossValueUsd: number | null;
};

export type AiValueSummary = {
  grossValueUsd: number | null;
};

function minutesToUsd(minutes: number, hourlyRateUsd: number) {
  return (minutes / 60) * hourlyRateUsd;
}

function workflowMinutesForActivity(
  workflow: WorkflowValueWorkflow,
  activity: ValueActivity
): { minutes: number | null; source: "llm" | "manual" | null } {
  if (activity === "build") {
    return {
      minutes: workflow.estimatedBuildMinutesSaved,
      source: workflow.timeSavedBuildSource,
    };
  }
  return {
    minutes: workflow.estimatedMinutesSaved,
    source: workflow.timeSavedSource,
  };
}

export function computeWorkflowAiValueRows(
  buildWorkflowIds: ReadonlySet<string>,
  workflows: readonly WorkflowValueWorkflow[],
  executionCounts: ReadonlyMap<string, number>,
  hourlyRateUsd: number
): WorkflowAiValueRow[] {
  return workflows
    .map((workflow) => {
      const workflowId = workflow.id;

      const byActivity = {} as Record<ValueActivity, WorkflowActivityValue>;
      let totalGross = 0;
      let hasGross = false;

      for (const activity of VALUE_ACTIVITIES) {
        const { minutes, source } = workflowMinutesForActivity(
          workflow,
          activity
        );
        // Build value is one-time; run minutes are per successful execution.
        const eventCount =
          activity === "build"
            ? buildWorkflowIds.has(workflowId)
              ? 1
              : 0
            : (executionCounts.get(workflowId) ?? 0);
        const grossValueUsd =
          minutes != null && eventCount > 0
            ? minutesToUsd(
                activity === "build" ? minutes : minutes * eventCount,
                hourlyRateUsd
              )
            : null;
        if (grossValueUsd != null) {
          totalGross += grossValueUsd;
          hasGross = true;
        }

        byActivity[activity] = {
          eventCount,
          minutesPerEvent: minutes,
          source,
          grossValueUsd,
        };
      }

      return {
        workflowId,
        workflowName: workflow.name,
        spaceId: workflow.spaceId,
        byActivity,
        totalGrossValueUsd: hasGross ? totalGross : null,
      };
    })
    .sort(
      (a, b) =>
        (b.totalGrossValueUsd ?? 0) - (a.totalGrossValueUsd ?? 0) ||
        a.workflowName.localeCompare(b.workflowName)
    );
}

export function computeAiValueSummary(
  rows: readonly WorkflowAiValueRow[],
  activityFilter: ValueActivity | "all" = "all"
): AiValueSummary {
  let grossValueUsd = 0;
  let hasGross = false;

  for (const row of rows) {
    const activities =
      activityFilter === "all" ? VALUE_ACTIVITIES : [activityFilter];
    for (const activity of activities) {
      const value = row.byActivity[activity].grossValueUsd;
      if (value != null) {
        grossValueUsd += value;
        hasGross = true;
      }
    }
  }

  return {
    grossValueUsd: hasGross ? grossValueUsd : null,
  };
}

export function currencySymbol(currency: ProductivityCurrency) {
  return (
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? currency
  );
}

export function formatCurrencyValue(
  amount: number | null,
  currency: ProductivityCurrency
): string {
  if (currency === "USD") return formatEstimatedCostUsd(amount);
  if (amount == null) return "—";
  const minorDigits = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).resolvedOptions().maximumFractionDigits;
  const fractionDigits =
    minorDigits === 0 ? 0 : amount >= 100 ? 0 : amount >= 10 ? 1 : 2;
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatSignedCurrencyValue(
  amount: number | null,
  currency: ProductivityCurrency
): string {
  const formatted = formatCurrencyValue(amount, currency);
  return amount != null && amount > 0 ? `+${formatted}` : formatted;
}

export function productivityRateFromBudgets(
  budgets: readonly {
    scopeKind: string;
    aiProviderId: string | null;
    productivityHourlyRate: number | null;
  }[]
): number {
  const tenantBudget = budgets.find(
    (b) => b.scopeKind === "tenant" && b.aiProviderId == null
  );
  const rate = tenantBudget?.productivityHourlyRate;
  return rate != null && rate > 0 ? rate : DEFAULT_PRODUCTIVITY_HOURLY_RATE_USD;
}
